import { Button, Image as AntImage, Modal, Slider, Typography } from "antd";
import { useState, useEffect } from "react";
import { getDownloadURL, ref, uploadString } from "firebase/storage";
import { useTranslation } from "react-i18next";
import Cropper from "react-easy-crop";
import defaultLogo from "../../img/black-logo.png";
import { storage } from "../../firebase";
import { useGlobalToast } from "../../context/ToastProvider";
import { getFleetDetails, updateFleetDetails } from "../../services/fleetDetailsFirestore";

const MAX_LOGO_BYTES = 2 * 1024 * 1024;

function dataUrlByteSize(dataUrl = "") {
  const base64 = String(dataUrl || "").split(",")[1] || "";
  if (!base64) return 0;
  const padding = base64.endsWith("==") ? 2 : base64.endsWith("=") ? 1 : 0;
  return Math.max(0, Math.floor((base64.length * 3) / 4) - padding);
}

function resolveLogoUploadErrorMessage(error, t) {
  const code = String(error?.code || "").trim();
  const message = String(error?.message || "").trim();

  if (code === "storage/unauthorized" || message.includes("storage/unauthorized")) {
    return t("setup.logoPermissionDenied", {
      defaultValue: "You do not have permission to update this fleet logo. Only the fleet owner can update branding.",
    });
  }

  if (code === "storage/quota-exceeded" || message.includes("storage/quota-exceeded")) {
    return t("setup.logoStorageQuotaExceeded", {
      defaultValue: "Storage quota was exceeded. Please try again later.",
    });
  }

  return t("setup.errorUpdatingLogo", { defaultValue: "Error updating logo" });
}

// --- Función de recorte y conversión a PNG (200x200) ---
const getCroppedImg = (imageSrc, cropAreaPixels) => {
  return new Promise((resolve, reject) => {
    const image = new window.Image();
    image.src = imageSrc;
    image.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      const OUTPUT_SIZE = 200;
      canvas.width = OUTPUT_SIZE;
      canvas.height = OUTPUT_SIZE;

      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";

      // Dibujamos el recorte escalado a 200x200
      ctx.drawImage(
        image,
        cropAreaPixels.x * scaleX,
        cropAreaPixels.y * scaleY,
        cropAreaPixels.width * scaleX,
        cropAreaPixels.height * scaleY,
        0,
        0,
        OUTPUT_SIZE,
        OUTPUT_SIZE
      );

      // Exportamos a PNG
      const base64 = canvas.toDataURL("image/png");
      resolve(base64);
    };
    image.onerror = (err) => reject(err);
  });
};

export default function LogoPicker({ labelKey, onClose, onChange, fleetId, disabled = false }) {
  const toast = useGlobalToast();
  const { t } = useTranslation();
  const [preview, setPreview] = useState(defaultLogo);

  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const [isOpen, setIsOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    const fetchFleetLogo = async () => {
      try {
        const details = await getFleetDetails(fleetId);
        const dbLogo = details?.logo || null;
        setPreview(dbLogo || defaultLogo);
      } catch (error) {
        console.error("Error fetching fleet logo:", error);
        setPreview(defaultLogo);
      }
    };
    if (fleetId) {
      fetchFleetLogo();
    } else {
      setPreview(defaultLogo);
    }
  }, [fleetId]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (!String(file.type || "").startsWith("image/")) {
      toast({
        title: t("setup.invalidLogoFile", { defaultValue: "Please choose an image file." }),
        status: "error",
      });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
      setIsOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCropAndUpload = async () => {
    if (!fleetId) return;
    if (!croppedAreaPixels) {
      toast({
        title: t("setup.logoCropRequired", { defaultValue: "Please wait for the logo crop preview to finish loading." }),
        status: "warning",
      });
      return;
    }
    setIsUploading(true);
    try {
      const base64Logo = await getCroppedImg(imageSrc, croppedAreaPixels);
      if (dataUrlByteSize(base64Logo) > MAX_LOGO_BYTES) {
        toast({
          title: t("setup.logoMaxSize", { defaultValue: "The cropped logo must be smaller than 2 MB." }),
          status: "error",
        });
        return;
      }
      const logoRef = ref(storage, `fleets/${fleetId}/branding/logo.png`);
      await uploadString(logoRef, base64Logo, "data_url", {
        contentType: "image/png",
      });
      const logoUrl = await getDownloadURL(logoRef);
      setPreview(logoUrl);

      await updateFleetDetails(fleetId, { logo: logoUrl, logoPath: logoRef.fullPath });
      if (onChange) onChange(logoUrl);

      toast({
        title: t("setup.logoUpdated") || "Logo actualizado correctamente",
        status: "success",
      });

      const event = new CustomEvent("logoUpdated", { detail: logoUrl });
      window.dispatchEvent(event);

      setIsOpen(false);
      setImageSrc(null);
      setZoom(1);
      setCrop({ x: 0, y: 0 });
      if (onClose) onClose();
    } catch (error) {
      console.error("Error uploading logo:", error);
      toast({ error,
        title: resolveLogoUploadErrorMessage(error, t),
        status: "error",
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div>
      <Typography.Text strong>{t(labelKey)}</Typography.Text>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
        <AntImage
          src={preview}
          alt={t("setup.logoPreviewAlt")}
          width={60}
          height={60}
          preview={false}
          style={{ objectFit: "contain", border: "1px solid #ccc", borderRadius: 4 }}
        />

        <input
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          style={{ display: "none" }}
          id="logo-input"
        />

        <Button
          size="small"
          type="primary"
          disabled={!fleetId || disabled}
          onClick={() => document.getElementById("logo-input").click()}
        >
          {t("setup.selectLogo") || "Seleccionar logo"}
        </Button>

        {/* Modal para recorte */}
        <Modal
          open={isOpen}
          onCancel={() => {
            if (!isUploading) setIsOpen(false);
          }}
          onOk={handleCropAndUpload}
          okText={t("setup.uploadCroppedLogo") || "Recortar y subir"}
          cancelText={t("setup.cancel") || "Cancelar"}
          width={720}
          confirmLoading={isUploading}
          okButtonProps={{ disabled: isUploading }}
          cancelButtonProps={{ disabled: isUploading }}
        >
          {imageSrc && (
            <div style={{ position: "relative", width: "100%", height: 300, background: "#f5f5f5" }}>
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_, croppedAreaPixels) => setCroppedAreaPixels(croppedAreaPixels)}
                minZoom={0.5}
                maxZoom={3}
                restrictPosition={false}
                showGrid={false}
              />
              <div style={{ marginTop: 12 }}>
                <Slider value={zoom} min={0.5} max={3} step={0.01} onChange={setZoom} disabled={isUploading} />
              </div>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
}
